'use client';
import React, { startTransition } from 'react';
import { Watchlist, WatchlistManager, WatchlistManagerEvent } from '@benzinga/watchlist-manager';
import { SessionContext } from '@benzinga/session-context';
import { useIsUserLoggedIn } from '@benzinga/user-context';
import { UserManager, UserManagerEvent } from '@benzinga/user-manager';

function symbolsFromWatchlists(watchlists: Watchlist[]): string[] {
  const symbols: string[] = [];
  watchlists.forEach(portfolio => {
    if (symbols.length > 50) return;
    Array.isArray(portfolio?.symbols) && portfolio.symbols.forEach(item => symbols.push(item.symbol));
  });
  return Array.from(new Set(symbols));
}

export const useWatchlistsLoading = () => {
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [watchlistSymbols, setWatchlistSymbols] = React.useState<string[]>([]);
  const [watchlists, setWatchlists] = React.useState<Watchlist[]>([]);
  const session = React.useContext(SessionContext);
  const userManager = session.getManager(UserManager);
  const watchlistManager = session.getManager(WatchlistManager);
  const isLoggedIn = useIsUserLoggedIn();

  const loadWatchlists = React.useCallback(() => {
    (async () => {
      const response = await watchlistManager.getWatchlists();
      if (response.err) {
        setIsLoading(false);
      } else if (response.ok && Array.isArray(response.ok)) {
        setWatchlists(response.ok);
        setIsLoading(false);
      }
    })();
  }, [watchlistManager]);

  React.useEffect(() => {
    const storedSymbols = localStorage.getItem('WATCHLIST_SYMBOLS');
    if (storedSymbols) {
      setWatchlistSymbols(storedSymbols.split(','));
    }
  }, []);

  React.useEffect(() => {
    const subUser = userManager.subscribe((event: UserManagerEvent) => {
      if (event.type === 'user:user_update') loadWatchlists();
    });
    return () => subUser.unsubscribe();
  }, [userManager, loadWatchlists]);

  React.useEffect(() => {
    const subWatchlist = watchlistManager.subscribe((event: WatchlistManagerEvent) => {
      if (event.type === 'watchlist:updated_watchlists') {
        const tickers = symbolsFromWatchlists(event.watchlists);
        localStorage.setItem('WATCHLIST_SYMBOLS', tickers.join(','));
        startTransition(() => setWatchlistSymbols(tickers));
      }
    });
    return () => subWatchlist.unsubscribe();
  }, [watchlistManager]);

  return {
    isLoading,
    isLoggedIn,
    watchlistSymbols,
    watchlists,
  };
};
